import 'firebase/compat/auth';
import React, { useContext, useEffect, useState } from 'react';

import { CheckCircle, Email } from '@mui/icons-material';
import { Box, Button, CircularProgress, FormHelperText } from '@mui/material';
import { auth } from '../../api/firebase';
import { langContext } from '../../api/Lang';
import type { VerificationData } from '../../types';
import { isBenignAuthPersistenceError } from '../../api/authPersistence';
import { populateEmailVerificationIfNeeded } from './populateEmailVerificationIfNeeded';
import { useUserProfileStrings } from './userProfile-strings';
import { verificationAuthErrorMessage } from './verificationAuthErrors';

const VERIFICATION_EMAIL_COOLDOWN_MS = 60_000;

function verificationEmailCooldownStorageKey(uid: string): string {
    return `lunchbox.profileVerificationEmailCooldownUntil.${uid}`;
}

function readPersistedVerificationEmailCooldownUntil(uid: string | undefined): number {
    if (!uid || typeof localStorage === 'undefined') {
        return 0;
    }
    const key = verificationEmailCooldownStorageKey(uid);
    const raw = localStorage.getItem(key);
    if (!raw) {
        return 0;
    }
    const until = Number(raw);
    if (!Number.isFinite(until) || until <= Date.now()) {
        localStorage.removeItem(key);
        return 0;
    }
    return until;
}

function persistVerificationEmailCooldownUntil(uid: string, until: number): void {
    localStorage.setItem(verificationEmailCooldownStorageKey(uid), String(until));
}

function clearPersistedVerificationEmailCooldown(uid: string | undefined): void {
    if (!uid || typeof localStorage === 'undefined') {
        return;
    }
    localStorage.removeItem(verificationEmailCooldownStorageKey(uid));
}

type EmailVerificationActionsProps = {
    emailVerification: VerificationData | undefined;
    setVerificationError: (error: string) => void;
    setHasCheckedVerification: (hasChecked: boolean) => void;
    setSuccessType: (type: 'email_sent' | 'email_verified' | null) => void;
};

export default function EmailVerificationActions({ emailVerification, setVerificationError, setHasCheckedVerification, setSuccessType }: EmailVerificationActionsProps) {
    const profileStrings = useUserProfileStrings();
    const [sendLoading, setSendLoading] = useState(false);
    const [verifyLoading, setVerifyLoading] = useState(false);
    const [sendEmailCooldownUntil, setSendEmailCooldownUntil] = useState(0);
    const [sendEmailCooldownSecondsLeft, setSendEmailCooldownSecondsLeft] = useState(0);

    const [currentLang] = useContext(langContext);

    const sendEmailOnCooldown = sendEmailCooldownSecondsLeft > 0;

    // --- Database Integration ---
    const currentUserUid = auth.currentUser?.uid;

    useEffect(() => {
        const persistedUntil = readPersistedVerificationEmailCooldownUntil(currentUserUid);
        if (persistedUntil > 0) {
            setSendEmailCooldownUntil(persistedUntil);
        }
    }, [currentUserUid]);

    useEffect(() => {
        if (sendEmailCooldownUntil <= Date.now()) {
            setSendEmailCooldownSecondsLeft(0);
            return;
        }
        const tick = (): void => {
            const secondsLeft = Math.max(0, Math.ceil((sendEmailCooldownUntil - Date.now()) / 1000));
            setSendEmailCooldownSecondsLeft(secondsLeft);
            if (secondsLeft <= 0) {
                setSendEmailCooldownUntil(0);
                clearPersistedVerificationEmailCooldown(currentUserUid);
            }
        };
        tick();
        const id = window.setInterval(tick, 1000);
        return () => window.clearInterval(id);
    }, [sendEmailCooldownUntil, currentUserUid]);
    const isVerified = emailVerification?.verified;

    // --- Handlers ---
    const handleSendVerificationEmail = async () => {
        if (sendEmailOnCooldown || sendLoading) {
            return;
        }
        const cooldownUntil = Date.now() + VERIFICATION_EMAIL_COOLDOWN_MS;
        setSendEmailCooldownUntil(cooldownUntil);
        if (auth.currentUser?.uid) {
            persistVerificationEmailCooldownUntil(auth.currentUser.uid, cooldownUntil);
        }
        setVerificationError(''); // Clear any previous verification error
        setSuccessType(null); // Clear any previous success message
        setSendLoading(true);

        try {
            if (!auth.currentUser) {
                throw new Error('No user is currently signed in');
            }

            // Set the language code for the verification email
            auth.languageCode = currentLang;

            await auth.currentUser.sendEmailVerification({
                url: window.location.origin + '/order', // Redirect to order page after verification
                handleCodeInApp: false
            });

            setSuccessType('email_sent'); // Set success type for email sent
        } catch (err: any) {
            console.error('Error sending verification email:', err);
            setVerificationError(verificationAuthErrorMessage(err, profileStrings.sendVerificationEmailError, profileStrings));
            setSuccessType(null);
        } finally {
            setSendLoading(false);
        }
    };

    const handleRefreshVerificationStatus = async () => {
        if (verifyLoading) {
            return;
        }
        setVerificationError(''); // Clear any previous verification error
        setSuccessType(null); // Clear any previous success message
        setVerifyLoading(true);
        setHasCheckedVerification(true); // Mark that user has checked verification

        try {
            if (!auth.currentUser) {
                throw new Error('No user is currently signed in');
            }

            // Reload the user to get the latest email verification status from Firebase Auth
            try {
                await auth.currentUser.reload();
            } catch (reloadError) {
                if (!isBenignAuthPersistenceError(reloadError)) {
                    throw reloadError;
                }
            }

            if (auth.currentUser.emailVerified) {
                await populateEmailVerificationIfNeeded({
                    uid: auth.currentUser.uid,
                    isGoogleUser: auth.currentUser.providerData.some(p => p.providerId === 'google.com'),
                    existingVerification: emailVerification,
                });
                setSuccessType('email_verified'); // Set success type for email verified
            } else {
                setVerificationError(''); // Clear the verification error - let the parent component handle the default message
                setSuccessType(null); // Clear any success message when verification fails
            }
        } catch (err: any) {
            console.error('Error refreshing verification status:', err);
            setVerificationError(verificationAuthErrorMessage(err, profileStrings.checkVerificationStatusError, profileStrings));
            setSuccessType(null);
        } finally {
            setVerifyLoading(false);
        }
    };

    // --- Render Logic ---
    if (isVerified) {
        // Don't show anything when verified - the EmailVerification component will show the success message inline
        return null;
    }

    return (
        <Box className="account-figma-email-verification-actions">
            <Box className="account-figma-email-verification-actions__row">
                <Button
                    variant="contained"
                    onClick={handleSendVerificationEmail}
                    disabled={sendLoading || sendEmailOnCooldown}
                    startIcon={sendLoading ? <CircularProgress size={16} /> : <Email />}
                >
                    {profileStrings.verificationEmailButton}
                </Button>

                <Button
                    variant="outlined"
                    onClick={handleRefreshVerificationStatus}
                    disabled={verifyLoading}
                    startIcon={verifyLoading ? <CircularProgress size={16} /> : <CheckCircle />}
                >
                    {profileStrings.verifyButton}
                </Button>
            </Box>
            {sendEmailOnCooldown && (
                <FormHelperText className="account-figma-email-hint account-figma-email-cooldown-hint" component="p">
                    {profileStrings.verificationEmailRetryIn(sendEmailCooldownSecondsLeft)}
                </FormHelperText>
            )}
        </Box>
    );
}
